import React, { useState, useEffect } from 'react';
import { X, Plus, Trash2, Copy, Edit2, Folder, Calendar } from 'lucide-react';
import { Board } from '../types';
import { BoardStorage } from '../utils/storage';
import { authService } from '../services/authService';

interface BoardLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectBoard: (board: Board) => void;
  onCreateBoard: () => void;
  currentBoardId?: string;
}

const BoardLibrary: React.FC<BoardLibraryProps> = ({
  isOpen,
  onClose,
  onSelectBoard,
  onCreateBoard,
  currentBoardId
}) => {
  const [boards, setBoards] = useState<Board[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingTitle, setEditingTitle] = useState('');
  const storage = BoardStorage.getInstance();

  useEffect(() => {
    if (isOpen) {
      loadBoards();
    }
  }, [isOpen]);

  const loadBoards = async () => {
    try {
      const loadedBoards = await storage.getAllBoards(authService.getCurrentUser()?.id || null);
      setBoards(loadedBoards);
    } catch (error) {
      console.error('Failed to load boards:', error);
    }
  };

  const handleRename = async (boardId: string, newTitle: string) => {
    try {
      const board = boards.find(b => b.id === boardId);
      if (board) {
        const updatedBoard = { ...board, title: newTitle };
        await storage.saveBoard(updatedBoard, authService.getCurrentUser()?.id || null);
        setBoards(boards.map(b => b.id === boardId ? updatedBoard : b));
      }
      setEditingId(null);
    } catch (error) {
      console.error('Failed to rename board:', error);
    }
  };

  const handleDuplicate = async (boardId: string) => {
    try {
      const originalBoard = boards.find(b => b.id === boardId);
      if (originalBoard) {
        const newBoard = await storage.duplicateBoard(boardId, `${originalBoard.title} (Copy)`, authService.getCurrentUser()?.id || null);
        setBoards([newBoard, ...boards]);
      }
    } catch (error) {
      console.error('Failed to duplicate board:', error);
    }
  };

  const handleDelete = async (boardId: string) => {
    if (window.confirm('Are you sure you want to delete this board? This action cannot be undone.')) {
      try {
        await storage.deleteBoard(boardId, authService.getCurrentUser()?.id || null);
        setBoards(boards.filter(b => b.id !== boardId));
      } catch (error) {
        console.error('Failed to delete board:', error);
      }
    }
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  // Get theme from the parent wrapper
  const theme = document.querySelector('[data-theme]')?.getAttribute('data-theme') || 'dark';

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`
        rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] overflow-hidden transition-colors duration-200
        ${theme === 'light' ? 'bg-white' : 'bg-gray-800'}
      `}>
        {/* Header */}
        <div className={`
          flex items-center justify-between p-6 border-b transition-colors duration-200
          ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}
        `}>
          <h2 className={`
            text-xl font-bold flex items-center gap-2 transition-colors duration-200
            ${theme === 'light' ? 'text-gray-900' : 'text-white'}
          `}>
            <Folder size={24} />
            Fibre Library
          </h2>
          <button
            onClick={onClose}
            className={`
              p-2 rounded transition-colors duration-200
              ${theme === 'light'
                ? 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                : 'text-gray-400 hover:bg-gray-700 hover:text-white'
              }
            `}
          >
            <X size={20} />
          </button>
        </div>

        {/* Actions */}
        <div className={`
          p-4 border-b transition-colors duration-200
          ${theme === 'light' ? 'border-gray-200' : 'border-gray-700'}
        `}>
          <button
            onClick={() => {
              onCreateBoard();
              onClose();
            }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            <Plus size={16} />
            New Board
          </button>
        </div>

        {/* Board List */}
        <div className="p-4 overflow-y-auto max-h-96">
          {boards.length === 0 ? (
            <div className={`
              text-center py-8 transition-colors duration-200
              ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}
            `}>
              <Folder size={48} className="mx-auto mb-4 opacity-50" />
              <p>No boards created yet</p>
              <p className="text-sm">Click "New Board" to get started</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {boards.map((board) => (
                <div
                  key={board.id}
                  className={`
                    p-4 rounded-lg border transition-all duration-200
                    ${currentBoardId === board.id
                      ? `${theme === 'light' ? 'border-blue-500 bg-blue-50' : 'border-blue-500 bg-gray-700'}`
                      : `${theme === 'light'
                        ? 'bg-gray-50 border-gray-200 hover:border-gray-300 hover:bg-gray-100'
                        : 'bg-gray-700 border-gray-600 hover:border-gray-500 hover:bg-gray-650'
                      }`
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      {editingId === board.id ? (
                        <input
                          type="text"
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          onBlur={() => handleRename(board.id, editingTitle)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              handleRename(board.id, editingTitle);
                            }
                          }}
                          className={`
                            w-full px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors duration-200
                            ${theme === 'light'
                              ? 'bg-white border-gray-300 text-gray-900'
                              : 'bg-gray-600 border-gray-500 text-white'
                            }
                          `}
                          autoFocus
                        />
                      ) : (
                        <>
                          <button
                            onClick={() => {
                              onSelectBoard(board);
                              onClose();
                            }}
                            className="text-left w-full"
                          >
                            <h3 className={`
                              font-medium transition-colors duration-200
                              ${theme === 'light' ? 'text-gray-900 hover:text-blue-600' : 'text-white hover:text-blue-400'}
                            `}>
                              {board.title}
                              {currentBoardId === board.id && (
                                <span className={`
                                  ml-2 text-xs transition-colors duration-200
                                  ${theme === 'light' ? 'text-blue-600' : 'text-blue-400'}
                                `}>(Current)</span>
                              )}
                            </h3>
                            <div className={`
                              flex items-center gap-4 mt-1 text-sm transition-colors duration-200
                              ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}
                            `}>
                              <span className="flex items-center gap-1">
                                <Calendar size={12} />
                                Updated {formatDate(board.updatedAt)}
                              </span>
                              <span>{board.nodes.length} nodes</span>
                              <span>{board.edges.length} edges</span>
                            </div>
                          </button>
                        </>
                      )}
                    </div>

                    <div className="flex items-center gap-1 ml-4">
                      <button
                        onClick={() => {
                          setEditingId(board.id);
                          setEditingTitle(board.title);
                        }}
                        className={`
                          p-2 rounded transition-colors duration-200
                          ${theme === 'light'
                            ? 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'
                            : 'text-gray-400 hover:bg-gray-600 hover:text-white'
                          }
                        `}
                        title="Rename"
                      >
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDuplicate(board.id)}
                        className={`
                          p-2 rounded transition-colors duration-200
                          ${theme === 'light'
                            ? 'text-gray-600 hover:bg-gray-200 hover:text-gray-900'
                            : 'text-gray-400 hover:bg-gray-600 hover:text-white'
                          }
                        `}
                        title="Duplicate"
                      >
                        <Copy size={14} />
                      </button>
                      <button
                        onClick={() => handleDelete(board.id)}
                        className={`
                          p-2 rounded transition-colors duration-200
                          ${theme === 'light'
                            ? 'text-red-600 hover:bg-red-100 hover:text-red-700'
                            : 'text-red-400 hover:bg-gray-600 hover:text-red-300'
                          }
                        `}
                        title="Delete"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BoardLibrary;
